/* eslint-disable import/first */

import React from 'react';
import ReactDOM from 'react-dom';
import { ThemeProvider } from 'styled-components';

import AntMenu from 'antd/lib/menu';
import AntMessage from 'antd/lib/message';

import { Link } from 'react-router-dom';

import { stringify as stringifyQuery } from 'querystring';

import { IntercomProvider } from 'react-use-intercom';

import {
  Switcher as SystemCountrySwitcher,
} from './components/SystemCountry';

import tests from './helpers/tests';

import './helpers/library';

import 'hive-admin/src/components/FieldGooglePolygon';
import 'hive-admin/src/components/FieldWYSIWYG';
import 'hive-admin/src/components/FieldCodeEditor';

import Admin from './components/Admin';

import './antd.less';
import './admin.less';

import './components/PageArchiveTable.less';
import './components/PageArchiveGrid.less';

import './components/ActionProductsImport';
import './components/ActionDelete';
import './components/ActionPopulateFieldsFromTarget';
import './components/ActionSave';
import './components/ActionSaveAndReload';
import './components/ActionSidebar';
import './components/FieldChangeLog';
import './components/FieldStoreSubscription';
import './components/FieldSlug';
import './components/FieldTextAreaMonospace';
import './components/FieldUploadRefsDropzone';
import './components/FieldWorkingHours';
import './components/FieldGoogleAddress';
import './components/FieldTable';
import './components/FieldFreeShipping';
import './components/FieldPaymentMethods';
import './components/FieldProductAttributes';
import './components/FieldRadioVertical';
import './components/FieldRichText';
import './components/PagePOSOutlet';
import './components/PageStatistics';
import './components/FieldContentBuilder';
import './components/PageOnboarding';
import './components/FieldCheckoutCardInput';
import './components/FieldRadioAdvanced';
import './components/PageArchiveTable';
import './components/FieldColorPicker';
import './components/FieldCodeEditor';

import StatisticsPages from './routes/statistics';
import AnalyticsPage from './routes/analytics';
import UserPages from './routes/users';
import StorePages from './routes/stores';
import PagesPages from './routes/pages';
import ContentBuilderPages from './routes/contentbuilder';
import CategoriesPages from './routes/categories';
import CollectionsPages from './routes/collections';
import DiscountCodesPages from './routes/discountcodes';
import OrdersPages from './routes/orders';
import ProductPage from './routes/products';
import ProductsImports from './routes/products-imports';
import ReferralsPages from './routes/referrals';
import ShippingCompaniesPages from './routes/shippingcompanies';
import ShippingCompaniesImportsPages from './routes/shippingcompaniesimports';
import SettingsPage, { aiPages } from './routes/settings';
import PayoutPage from './routes/payout/index';
import CyclesPage from './routes/cycles';
import AdjustmentPage from './routes/adjustments';
import ApprovePage from './routes/approve';
import LanguagesPages from './routes/languages';
// import DevelopmentPage from './routes/development';
import PricingPages from './routes/pricing';
import OnboardingPage from './routes/onboarding';
import CheckoutPage from './routes/checkout';
import CustomerPages from './routes/customers';
import ReviewPages from './routes/reviews';
import AbandonedCarts from './routes/abandoned-carts';

import theme from './theme';

import { renderLogo, renderSidebarLogo } from './components/Logo';

import packageJson from '../package.json';

const {
  viewerIsNotAdmin,
  viewerIsAdminAndDoesNotHavePermission,
} = tests;

const {
  REACT_APP_SERVER_HOST,
  REACT_APP_SERVER_PROTOCOL,
  REACT_APP_HTTP_PORT,
  REACT_APP_API_PATH,
  REACT_APP_PUBLIC_URL = '',
  REACT_APP_DXTOOLS_ENV_SYMBOL = '',
} = process.env;

const SERVER_URL = `${
  REACT_APP_SERVER_PROTOCOL
}://${
  REACT_APP_SERVER_HOST
}${
  REACT_APP_HTTP_PORT === '80' ? '' : `:${REACT_APP_HTTP_PORT}`
}`;

const base = window.ADMIN_BASE = `${REACT_APP_PUBLIC_URL}`;
const restBase = (
    REACT_APP_HTTP_PORT === '80'
  ? `${SERVER_URL}${REACT_APP_API_PATH}`
  : `${REACT_APP_API_PATH}`
);

const admin = Admin.create({
  base,
  restBase,
  restBaseServer: `${SERVER_URL}${REACT_APP_API_PATH}`,
  titlePrefix: `Admin${
    REACT_APP_DXTOOLS_ENV_SYMBOL
    ? ` ${REACT_APP_DXTOOLS_ENV_SYMBOL}`
    : ''
  } | `,
  checkoutCardInputRef: React.createRef(),
  validateViewer: async (viewer, props) => {
    await Promise.all([
      (async () => {
        if (viewer) {
          viewer.settings = await (
            props.client
            .get(`/settings/general?${stringifyQuery({
              includeInternalStores: true,
            })}`)
            .then(res => res.data)
          );
        }
        if (viewer.role === 'ADMIN') {
          viewer.permissions = await (
            props.client
            .get('/settings/permissions')
            .then(res => res.data)
          );
        }
      })(),
      (async () => {
        if (viewer) {
          viewer.languages = await (
            props.client
            .get(`/languages?query=${JSON.stringify({
              sort: { name: 1 },
            })}`)
            .then(res => res.data?.data?.sort?.((a, b) => (
                a._id === 'en' ? -1000000
              : b._id === 'en' ? 1000000
              : (a > b ? 1 : a < b ? -1 : 0)
            )))
          );
        }
      })(),
      (async () => {
        if (viewer?.role === 'SELLER') {
          viewer.storeData = await (
            props.client.get('/stores/me').then(res => res.data)
          );
        }
      })(),
    ]);
    return viewer;
  },
  sidebarProps: { renderLogo: renderSidebarLogo },
  passwordSetSuccessRedirectPath: '/login',
  accountActivationSuccessRedirectPath: '/login',
  clientVersion: packageJson.version,
  structure: [
    ['PageLogin', {
      renderBeforeForm: renderLogo,
      redirect: [['redirect.authorized']],
      renderAfterForm: props => (
        <>
          <p className="after-form" style={{ marginBottom: '0.3em' }}>
            Lost your password? Click
            {' '}
            <Link to={props.passwordResetPath}>
              here
            </Link>
            {' '}
            to set a new one.
          </p>
          <p className="after-form">
            If you dont yet have an account, click
            {' '}
            <a
              href="https://packman.ai/get-started"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>
            {' '}
            to sign up instead.
          </p>
        </>
      ),
    }],
    ['Group', {
      path: '/SYSTEM_LANGUAGE_PICKER',
      renderNavigationMenuItem: (item, navigation) => (
        <AntMenu.Item
          key={item.id}
          onMouseEnter={navigation.handleMenuItemMouseEnter}
        >
          <SystemCountrySwitcher
            searchParams={navigation.props.searchParams}
            location={navigation.props.location}
          />
        </AntMenu.Item>
      ),
      exact: true,
      redirect: [
        ['redirect.unauthorized'],
        ['redirect.always', '/users/me'],
      ],
      skip: [
        viewerIsNotAdmin,
      ],
    }],
    CheckoutPage,
    ['Page404', {
      path: '/signup',
      exact: true,
      hidden: true,
      hideHeader: true,
      hideSidebar: true,
      // nginx redirects this path, this only covers in-app navigation
      redirect: [
        () => {
          window.location.replace('https://packman.ai/get-started');
          return '';
        },
      ],
    }],
    ['Page', {
      hidden: true,
      path: '/',
      exact: true,
      redirect: [(props) => {
        if (props.viewer && props.viewer.role === 'SELLER') {
          return '/store/orders';
        }
        return '/orders';
      }],
    }],
    ['PagePasswordReset', {
      renderBeforeForm: renderLogo,
      redirect: [['redirect.authorized']],
    }],
    ['PagePasswordSet', {
      renderBeforeForm: renderLogo,
    }],
    ['PageAccountActivation', {
      renderBeforeForm: renderLogo,
      accountActivationSuccessRedirectPath: '/',
    }],
    ...StorePages,
    ['Group', {
      title: 'Orders',
      icon: 'shopping',
      skip: [
        viewerIsAdminAndDoesNotHavePermission('orders'),
      ],
      pages: [
        ...OrdersPages,
        ...AbandonedCarts,
        ...CustomerPages,
        ...ReviewPages,
      ],
    }],
    ['Group', {
      title: 'Analytics',
      icon: 'dashboard',
      skip: [
        viewerIsAdminAndDoesNotHavePermission('analytics'),
      ],
      pages: [
        AnalyticsPage,
        ...StatisticsPages,
      ],
    }],
    ['Group', {
      title: 'Website',
      icon: 'layout',
      skip: [
        viewerIsAdminAndDoesNotHavePermission('website'),
      ],
      pages: [
        ...PagesPages,
        ...ContentBuilderPages,
        ...LanguagesPages,
      ],
    }],
    ['Group', {
      title: 'Catalogue',
      icon: 'unordered-list',
      skip: [
        viewerIsAdminAndDoesNotHavePermission('catalogue'),
      ],
      pages: [
        ...ProductPage,
        ...ProductsImports,
        ...CategoriesPages,
        ...CollectionsPages,
        ...DiscountCodesPages,
        ...ReferralsPages,
      ],
    }],
    ['Group', {
      title: 'Shipping',
      icon: 'car',
      // skip: [viewerIsNotAdmin],
      skip: [
        viewerIsAdminAndDoesNotHavePermission('shipping'),
      ],
      pages: [
        ...PricingPages,
        ...ShippingCompaniesPages,
        ...ShippingCompaniesImportsPages,
      ],
    }],
    ['Group', {
      title: 'Users',
      icon: 'user',
      hidden: [
        viewerIsNotAdmin,
        viewerIsAdminAndDoesNotHavePermission('users'),
      ],
      pages: [
        ...UserPages,
      ],
    }],
    ['Group', {
      title: 'Billing & Invoicing',
      icon: 'dollar',
      hidden: [
        // TODO: change permissions
        viewerIsNotAdmin,
        viewerIsAdminAndDoesNotHavePermission('users'),
      ],
      pages: [
        ...PayoutPage,
        ...CyclesPage,
        // ...AdjustmentPage,
      ],
    }],
    SettingsPage,
    aiPages,
    ApprovePage,
    // DevelopmentPage,
    OnboardingPage,
    ['Page404', {
      redirect: [
        // ['redirect.unauthorized'],
        props => {
          if (props?.viewer) {
            if (props.viewer.role === 'SELLER') {
              return '/store/orders';
            }
            if (props.viewer.role === 'ADMIN') {
              if (props.history.length > 2) {
                props.history.goBack();
                AntMessage.info(
                  `The page your requested either
                  doesn’t exist or you don’t have permission to view it.`
                )
                return '';
              }
              return '/users/me';
            }
            return '/users/me';
          }
          return '/login';
        },
      ],
    }],
  ],
});

ReactDOM.render(
  (
    <>
      <ThemeProvider theme={theme}>
        <IntercomProvider appId={process.env.REACT_APP_INTERCOM_APP_ID}>
          {admin}
        </IntercomProvider>
      </ThemeProvider>
    </>
  ),
  document.getElementById('root'),
);
